/**
 * StaffAvatar — circular profile image with a graceful "initials" fallback.
 *
 * Used by:
 *   • <StaffProfileTab /> (large, 80–96px)
 *   • <UsersTab /> rows (small, 28px)
 *   • Any future surface (Active Sessions, Chat handoff cards, etc.)
 *
 * If `src` is empty/null/falsy, we render a deterministic coloured circle
 * with the user's initials. Otherwise we render the <img>. Falls back to
 * initials again if the <img> errors (e.g. 404).
 */
import { useState, useEffect } from "react";

function initialsFor(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Deterministic hue from the name so each staff member gets a stable colour.
function hueFor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return h % 360;
}

const SIZE_CLASS = {
  xs: "w-7 h-7 text-[10px]",
  sm: "w-9 h-9 text-xs",
  md: "w-12 h-12 text-sm",
  lg: "w-20 h-20 text-xl",
  xl: "w-24 h-24 text-2xl",
};

export default function StaffAvatar({
  src,
  name = "",
  size = "sm",
  className = "",
  "data-testid": testId,
}) {
  // Re-mount the <img> whenever the src changes so a fresh upload busts
  // the old 404'd state.
  const [errored, setErrored] = useState(false);
  useEffect(() => { setErrored(false); }, [src]);

  const sizeCls = SIZE_CLASS[size] || SIZE_CLASS.sm;
  const initials = initialsFor(name);
  const hue = hueFor(name || "?");
  const bg = `hsl(${hue}, 65%, 55%)`;

  if (src && !errored) {
    return (
      <img
        src={src}
        alt={name ? `${name}'s profile photo` : "Profile photo"}
        loading="lazy"
        onError={() => setErrored(true)}
        className={`rounded-full object-cover bg-slate-200 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 ${sizeCls} ${className}`}
        data-testid={testId}
      />
    );
  }
  return (
    <div
      className={`rounded-full flex items-center justify-center font-semibold text-white border border-white/20 ${sizeCls} ${className}`}
      style={{ backgroundColor: bg }}
      title={name}
      data-testid={testId}
    >
      {initials}
    </div>
  );
}
